import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { PRODUCTS } from '../store/actionTypes';
import { Plan, Product } from './models/product';

export const products = createApi({
  reducerPath: PRODUCTS,
  baseQuery,
  tagTypes: ['Plans'],
  endpoints: builder => ({
    getPlans: builder.query<Plan[], void>({
      query: variables => ({
        document: gql`
          query GetPlans($page: Int, $limit: Int) {
            getPlans(page: $page, limit: $limit) {
              name {
                language
                text
              }
              description {
                language
                text
              }
              offers {
                course {
                  offer
                  type
                  value
                  text {
                    language
                    text
                  }
                }
                recording {
                  offer
                  type
                  value
                }
              }
              price {
                planId
                currencies {
                  id
                  code
                  amount
                  amountInUnit
                  displayFormat
                }
                id
              }
              duration {
                size
                unit
              }
              code
              variants
              rank {
                index
                promote
              }
              renewal
              id
              meta {
                active
                created
                updated
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getPlans,
    }),
    getProducts: builder.query<
      Product[],
      {
        page?: number;
        limit?: number;
        search?: string;
        subjectIds?: string;
        gradeLevelIds?: string;
        timeOfDay?: string;
        sortBy?: 'EARLIEST' | 'LATEST';
      }
    >({
      query: variables => ({
        document: gql`
          query GetProducts(
            $page: Int
            $limit: Int
            $search: String
            $subjectIds: String
            $gradeLevelIds: String
            $timeOfDay: String
            $sortBy: SortOrder
          ) {
            getProducts(
              page: $page
              limit: $limit
              search: $search
              subjectIds: $subjectIds
              gradeLevelIds: $gradeLevelIds
              timeOfDay: $timeOfDay
              sortBy: $sortBy
            ) {
              id
              slug
              prices {
                planId
                productId
                currencies {
                  id
                  code
                  amount
                  amountInUnit
                  displayFormat
                }
                id
              }
              category
              name {
                language
                displayName
                categoryDisplayName
              }
              classroom {
                size
                session {
                  start
                  end
                  terms
                }
                subject
                gradeLevel
                subjectData {
                  id
                  name {
                    text
                  }
                  code
                }
              }
              meta {
                active
                created
                updated
              }
              productEventsData {
                attendees {
                  organizer
                  user {
                    profile {
                      firstname
                      lastname
                      bio
                      ids {
                        name
                        value
                      }
                      username
                      certifications {
                        category
                        issuedByName
                        countryOfOrigin
                      }
                      avatar {
                        contentUrl
                        medium
                      }
                    }
                    id
                    email
                    country
                    preferences {
                      knowsLanguages
                    }
                  }
                }
                endTime {
                  dateTime
                }
                startTime {
                  dateTime
                }
                lessons {
                  participants {
                    userId
                  }
                  classroom {
                    capacity
                  }
                }
              }
              description {
                displayName
                language
                text
              }
              image {
                baseUrl
              }
              title {
                text
                displayName
                language
              }
              media {
                contentUrl
              }
              pagination {
                items
                pages
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProducts,
    }),
    getProductBySlug: builder.query<Product, { slug: string }>({
      query: variables => ({
        document: gql`
          query GetProductBySlug($slug: String!) {
            getProductBySlug(slug: $slug) {
              id
              name {
                language
                displayName
                categoryDisplayName
              }
              description {
                language
                text
              }
              slug
              category
              productEvents
              classroom {
                size
                session {
                  start
                  end
                  terms
                }
                subject
                gradeLevel
                subjectData {
                  id
                  name {
                    text
                  }
                  code
                }
                gradeLevelData {
                  _id
                  level {
                    text
                  }
                }
              }
              prices {
                productId
                planId
                currencies {
                  id
                  code
                  amount
                  amountInUnit
                  displayFormat
                }
                description {
                  language
                  text
                }
                plan {
                  name {
                    language
                    text
                  }
                  description {
                    language
                    text
                  }
                  offers {
                    course {
                      offer
                      type
                      value
                      text {
                        language
                        text
                      }
                    }
                    recording {
                      offer
                      type
                      value
                      text {
                        language
                        text
                      }
                    }
                  }
                  duration {
                    size
                    unit
                  }
                  code
                  rank {
                    index
                    promote
                  }
                  renewal
                  id
                }
                id
              }
              plan {
                name {
                  language
                  text
                }
                description {
                  language
                  text
                }
                offers {
                  course {
                    offer
                    type
                    value
                    text {
                      language
                      text
                    }
                  }
                  recording {
                    offer
                    type
                    value
                    text {
                      language
                      text
                    }
                  }
                }
                duration {
                  size
                  unit
                }
                code
                rank {
                  index
                  promote
                }
                renewal
                id
              }
              meta {
                active
                created
                updated
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductBySlug,
    }),
    getProductsByProductEventId: builder.query<Product[], { productEventId: string }>({
      query: variables => ({
        document: gql`
          query GetProductsByProductEventId($productEventId: String!) {
            getProductsByProductEventId(productEventId: $productEventId) {
              id
              name {
                language
                displayName
                categoryDisplayName
              }
              description {
                language
                text
              }
              slug
              category
              productEvents
              classroom {
                size
                session {
                  start
                  end
                  terms
                }
                subject
                gradeLevel
                subjectData {
                  id
                  name {
                    text
                  }
                  code
                }
                gradeLevelData {
                  _id
                  level {
                    text
                  }
                }
              }
              prices {
                productId
                planId
                currencies {
                  id
                  code
                  amount
                  amountInUnit
                  displayFormat
                }
                description {
                  language
                  text
                }
                plan {
                  name {
                    language
                    text
                  }
                  description {
                    language
                    text
                  }
                  offers {
                    course {
                      offer
                      type
                      value
                      text {
                        language
                        text
                      }
                    }
                    recording {
                      offer
                      type
                      value
                      text {
                        language
                        text
                      }
                    }
                  }
                  duration {
                    size
                    unit
                  }
                  code
                  rank {
                    index
                    promote
                  }
                  renewal
                  id
                }
                id
              }
              meta {
                active
                created
                updated
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.getProductsByProductEventId,
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetProductsQuery,
  useGetProductBySlugQuery,
  useGetProductsByProductEventIdQuery,
} = products;
